/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */

// @flow
import type { Node } from 'react';

import React from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import { Input } from 'reactstrap';
import { isEmpty } from 'lodash';
import config, { GOOGLE_MAPS_API_KEY } from '#support/app/config.js';
import predictionText from '#support/models/places/predictionText.js';
import { STATUS_EMPTY, STATUS_OPEN } from '#support/models/places/typeaheadReducer.js';
import usePlacesSuggestions from './usePlacesSuggestions.js';
import { highlighted, item, menu, note, secondary, wrapper } from './AddressTypeahead.module.scss';

type SuggestingProps = {|
  input: Object,
  id: string,
  meta: Object,
  regionCode: ?string,
  disabled: boolean,
  inputClassName: ?string,
  setAddressFields: Function,
|};

type Props = {|
  input: Object,
  id: string,
  meta: Object,
  regionCode?: ?string,
  readOnly?: boolean,
  disabled?: boolean,
  inputClassName?: ?string,
  setAddressFields?: Function,
|};

const Suggesting = ({
  input,
  id,
  meta,
  regionCode,
  disabled,
  inputClassName,
  setAddressFields,
}: SuggestingProps): Node => {
  const {
    state,
    onQueryChange,
    onDismiss,
    resolve,
    highlightNextSuggestion,
    highlightPreviousSuggestion,
    highlightSuggestionAt,
    enterSelectionIndex,
  } = usePlacesSuggestions({ regionCode });

  const { touched, error, form } = meta;
  const hasError = touched && !isEmpty(error);
  const listId = `${id}-suggestions`;
  const isOpen = state.status === STATUS_OPEN;
  const activeId =
    isOpen && state.highlightedIndex >= 0 ? `${listId}-${state.highlightedIndex}` : undefined;

  const select = prediction => {
    resolve(prediction).then(values => {
      if (!values) {
        return;
      }
      input.onChange(values.street);
      setAddressFields(form, values);
    });
  };

  const onKeyDown = event => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      highlightNextSuggestion();
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      highlightPreviousSuggestion();
      return;
    }
    if (event.key === 'Escape') {
      onDismiss();
      return;
    }
    if (event.key === 'Enter') {
      const index = enterSelectionIndex();
      // Nothing highlighted means Enter is not ours — it must still advance the wizard.
      if (index === null) {
        return;
      }
      event.preventDefault();
      select(state.suggestions[index]);
    }
  };

  /* eslint-disable react/jsx-props-no-spreading */
  return (
    <div className={wrapper}>
      <Input
        {...input}
        id={id}
        type="text"
        invalid={hasError}
        className={inputClassName}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeId}
        onChange={event => {
          input.onChange(event);
          onQueryChange(event.target.value);
        }}
        onBlur={event => {
          // Close the list, but never rewrite the value.
          onDismiss();
          input.onBlur(event);
        }}
        onKeyDown={onKeyDown}
      />
      {isOpen && (
        <ul className={menu} id={listId} role="listbox">
          {state.suggestions.map((suggestion, index) => (
            <li
              key={suggestion.placeId}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === state.highlightedIndex}
              className={index === state.highlightedIndex ? `${item} ${highlighted}` : item}
              onMouseEnter={() => highlightSuggestionAt(index)}
              onMouseDown={event => {
                // mousedown, not click: blur fires first and would close the list.
                event.preventDefault();
                select(suggestion);
              }}
            >
              {predictionText(suggestion.mainText)}
              <span className={secondary}>{predictionText(suggestion.secondaryText)}</span>
            </li>
          ))}
        </ul>
      )}
      {state.status === STATUS_EMPTY && (
        <ul className={menu} id={listId} role="listbox">
          <li className={note}>No matching addresses — keep typing to enter it manually</li>
        </ul>
      )}
    </div>
  );
  /* eslint-enable react/jsx-props-no-spreading */
};

const AddressTypeahead = ({
  input,
  id,
  meta,
  regionCode = null,
  readOnly = false,
  disabled = false,
  inputClassName = null,
  setAddressFields = () => {},
}: Props): Node => {
  const { touched, error } = meta;

  /* eslint-disable react/jsx-props-no-spreading */
  if (readOnly) {
    return (
      <div className={wrapper}>
        <Input
          {...input}
          id={id}
          type="text"
          invalid={touched && !isEmpty(error)}
          className={inputClassName}
          readOnly
          disabled={disabled}
        />
      </div>
    );
  }
  /* eslint-enable react/jsx-props-no-spreading */

  return (
    <APIProvider apiKey={config(GOOGLE_MAPS_API_KEY)}>
      <Suggesting
        input={input}
        id={id}
        meta={meta}
        regionCode={regionCode}
        disabled={disabled}
        inputClassName={inputClassName}
        setAddressFields={setAddressFields}
      />
    </APIProvider>
  );
};

export default AddressTypeahead;
